import { Container, Graphics, Sprite } from 'pixi.js';
import { COLORS } from '../app/theme';
import type { Atlas } from '../assets/AtlasLoader';

/** CardsLow frames are 117×170 → this height-to-width ratio keeps cards undistorted. */
export const CARD_ASPECT = 170 / 117;

/** The face-down card frame name (sentinel used by the server for hidden cards). */
const BACK = 'CardsLow/BB';

/**
 * Maps a 2-char server card code ("AS", "7D", "TS", "BB") to its atlas frame name. Anything that
 * isn't a 2-char code (empty, malformed) falls back to the back, so a bad value never throws.
 */
export function cardFrameName(code: string): string {
  const c = code?.trim().toUpperCase();
  return c && c.length === 2 ? `CardsLow/${c}` : BACK;
}

/** A single card sprite sized to `width` (renders the back for "BB"/unknown/missing-frame). */
export function cardSprite(atlas: Atlas | null, code: string, width: number): Container {
  const c = new Container();
  const tex = atlas?.frames.get(cardFrameName(code)) ?? atlas?.frames.get(BACK);
  if (tex) {
    const h = width * CARD_ASPECT;
    const s = new Sprite(tex);
    s.width = width;
    s.height = h;
    c.addChild(s);
    // The CardsLow art has square corners; clip to a soft rounded-rect so cards read as real cards.
    const radius = Math.max(4, width * 0.08);
    const mask = new Graphics().roundRect(0, 0, width, h, radius).fill({ color: 0xffffff });
    c.addChild(mask);
    s.mask = mask;
  }
  return c;
}

/**
 * A left-to-right overlapped row of cards (later cards drawn on top). Returns a Container; its width
 * is `cardWidth + (n-1)*cardWidth*(1-overlap)`.
 */
export function handRow(atlas: Atlas | null, codes: string[], cardWidth: number, overlap = 0.42): Container {
  const c = new Container();
  const step = cardWidth * (1 - overlap);
  codes.forEach((code, i) => {
    const card = cardSprite(atlas, code, cardWidth);
    card.position.set(i * step, 0);
    c.addChild(card);
  });
  return c;
}

export interface HandFanOpts {
  cardWidth: number;
  /** Total fan angle (radians) from the first card to the last. Default ~9°. */
  spread?: number;
  /** Soft drop-shadow behind each card so it reads off a busy felt. */
  shadow?: boolean;
  /**
   * Hard cap on the fan's total visual width (px). When the natural overlap would run wider, the
   * per-card step tightens so the whole hand fits — "more cards ⇒ more compact". Card size, rotation,
   * and the arc are unchanged. Omit to keep the natural (uncapped) spacing.
   */
  maxWidth?: number;
}

/**
 * A gently fanned hand: cards spread left-to-right with a slight rotation and a small upward arc, the
 * overlap loosening for a 2-card hand (Blackjack's norm) and tightening as the hand grows so it never
 * runs too wide. The fan is centered on local x=0 (its top edge sits at local y≈0), so the caller just
 * positions it at the hand's center. Each card is its own child Container — `scaleIn()` the children to
 * animate a deal. Cards rotate/scale about their bottom-center.
 */
export function handFan(atlas: Atlas | null, codes: string[], opts: HandFanOpts): Container {
  const c = new Container();
  const n = codes.length;
  if (!n) return c;
  const w = opts.cardWidth;
  const h = w * CARD_ASPECT;
  const spread = opts.spread ?? 0.16;
  // Loosen the per-card step for small hands, tighten for big ones (keeps wide hands on-screen).
  const gapFrac = n <= 2 ? 0.82 : n <= 4 ? 0.7 : 0.56;
  let step = w * gapFrac;
  // Cap the total width if asked: shrink the step so (n-1)*step + w === maxWidth (floor stops reversal).
  if (opts.maxWidth && n > 1 && (n - 1) * step + w > opts.maxWidth) {
    step = Math.max((opts.maxWidth - w) / (n - 1), w * 0.18);
  }
  const angStep = n > 1 ? spread / (n - 1) : 0;
  const mid = (n - 1) / 2;
  codes.forEach((code, i) => {
    const offset = i - mid;
    const holder = new Container();
    if (opts.shadow) {
      const sh = new Graphics().roundRect(0, 0, w, h, 8).fill({ color: COLORS.scrim, alpha: 0.35 });
      sh.position.set(3, 5);
      holder.addChild(sh);
    }
    holder.addChild(cardSprite(atlas, code, w));
    // Rotate/scale about the card's bottom-center, and place that point along a gentle upward arc.
    holder.pivot.set(w / 2, h);
    holder.rotation = offset * angStep;
    holder.position.set(offset * step, h + Math.abs(offset) * (w * 0.045));
    c.addChild(holder);
  });
  return c;
}
